import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, Building2, Users, Target, Briefcase } from 'lucide-react';
import { workspaceService } from '../../services/workspaceService';

const WorkspaceSetup = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingWorkspace, setCheckingWorkspace] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    teamSize: 'solo',
    industryCategory: '',
    primaryUseCase: '',
    description: ''
  });

  // Check for existing workspace on mount
  useEffect(() => {
    let mounted = true;
    let timeoutId;

    const checkWorkspace = async () => {
      if (authLoading) return;
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Set a 3-second timeout to prevent infinite loading
      timeoutId = setTimeout(() => {
        if (mounted) {
          console.log('Workspace check timeout - showing form');
          setCheckingWorkspace(false);
        }
      }, 3000);

      try {
        const workspaces = await workspaceService?.getUserWorkspaces(user?.id);
        
        if (!mounted) return;

        // Clear timeout if we get a response
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (workspaces && workspaces?.length > 0) {
          navigate('/project-dashboard', { replace: true });
        } else {
          setCheckingWorkspace(false);
        }
      } catch (err) {
        console.error('Workspace check error:', err);
        if (mounted) {
          // Clear timeout on error
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          // Show form even on error
          setCheckingWorkspace(false);
        }
      }
    };

    checkWorkspace();

    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!user) {
      setError('User not authenticated');
      return;
    }

    if (!formData?.name?.trim()) {
      setError('Please enter a workspace name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await workspaceService?.createWorkspace({
        name: formData?.name,
        teamSize: formData?.teamSize,
        industryCategory: formData?.industryCategory,
        primaryUseCase: formData?.primaryUseCase,
        description: formData?.description,
        ownerId: user?.id
      });

      navigate('/project-dashboard', { replace: true });
    } catch (err) {
      console.error('Workspace creation error:', err);
      setError(err?.message || 'Failed to create workspace. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || checkingWorkspace) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-2xl mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Your Workspace</h1>
          <p className="text-gray-600">Set up your workspace to start building amazing apps</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Workspace Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
                placeholder="Enter workspace name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-2">
                Team Size
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="teamSize"
                  name="teamSize"
                  value={formData?.teamSize}
                  onChange={(e) => setFormData({ ...formData, teamSize: e?.target?.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="solo">Solo (Just me)</option>
                  <option value="small">Small (2-10 people)</option>
                  <option value="medium">Medium (11-50 people)</option>
                  <option value="large">Large (50+ people)</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="industryCategory" className="block text-sm font-medium text-gray-700 mb-2">
                Industry Category
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="industryCategory"
                  name="industryCategory"
                  value={formData?.industryCategory}
                  onChange={(e) => setFormData({ ...formData, industryCategory: e?.target?.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Select industry</option>
                  <option value="technology">Technology</option>
                  <option value="education">Education</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="marketing">Marketing</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="primaryUseCase" className="block text-sm font-medium text-gray-700 mb-2">
                Primary Use Case
              </label>
              <div className="relative">
                <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  id="primaryUseCase"
                  name="primaryUseCase"
                  value={formData?.primaryUseCase}
                  onChange={(e) => setFormData({ ...formData, primaryUseCase: e?.target?.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Select use case</option>
                  <option value="web_app">Web Application</option>
                  <option value="mobile_app">Mobile Application</option>
                  <option value="saas">SaaS Product</option>
                  <option value="portfolio">Portfolio Website</option>
                  <option value="landing_page">Landing Page</option>
                  <option value="internal_tool">Internal Tool</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                rows={4}
                placeholder="Tell us about your workspace..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating Workspace...
                </>
              ) : (
                'Create Workspace'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-600 mt-6 text-sm">
          You can always update these settings later from your workspace dashboard
        </p>
      </div>
    </div>
  );
};

export default WorkspaceSetup;